import { isProPath } from '@/lib/section'
import type {
  Article,
  Author,
  Category,
  NavItem,
  Resolved,
  Service,
  Settings,
} from '@/types'

import servicesJson from './services.json'
import articlesJson from './articles.json'
import categoriesJson from './categories.json'
import authorsJson from './authors.json'
import navJson from './nav.json'
import navProJson from './nav-pro.json'
import settingsJson from './settings.json'

export const services = servicesJson as unknown as Service[]
export const articles = articlesJson as unknown as Article[]
export const categories = categoriesJson as unknown as Category[]
export const authors = authorsJson as unknown as Author[]
export const nav = navJson as unknown as NavItem[]
export const navPro = navProJson as unknown as NavItem[]
export const settings = settingsJson as unknown as Settings

/**
 * Menu a afficher pour un chemin donne : l'Espace Professionnels a sa propre
 * navigation (BtoB), le reste du site utilise le menu Particuliers (BtoC).
 * Repli sur le menu Particuliers si le menu pro n'a pas encore ete alimente.
 */
export function navFor(path: string): NavItem[] {
  if (isProPath(path) && navPro.length) return navPro
  return nav
}

// Chemins des pages "fixes" (ce sont aussi des entrees services dans le dataset).
export const PATHS = {
  home: '/',
  contact: '/contact-2/',
  merci: '/merci/',
  mentions: '/mentions-legales/',
  blog: '/information-actualites-articles/',
  quiSommesNous: '/qui-sommes-nous-menuiserie-mayenne-laval-la-baule/',
  pro: '/pro/',
} as const

// Index par chemin / id.
const serviceByPath = new Map(services.map((s) => [s.path, s]))
const articleByPath = new Map(articles.map((a) => [a.path, a]))
const categoryByPath = new Map(categories.map((c) => [c.path, c]))
const categoryById = new Map(categories.map((c) => [c.wp_id, c]))
const authorById = new Map(authors.map((a) => [a.wp_id, a]))

// content_html complet : reference UNIQUEMENT sous la garde import.meta.env.SSR.
// Cote client, cette garde vaut `false` : la branche (et donc l'import JSON) est
// eliminee par tree-shaking, le bundle client ne transporte pas les ~5 Mo de contenu.
// Le HTML de la page courante arrive cote client via l'etat serialise (voir lib/pageHtml.ts).
import contentHtmlJson from './content-html.json'
const ssrHtmlMap = contentHtmlJson as unknown as Record<string, string>
export function ssrHtml(path: string): string {
  if (!import.meta.env.SSR) return ''
  return ssrHtmlMap[normalizePath(path)] || ''
}

export function normalizePath(p: string): string {
  if (!p) return '/'
  let path = p.split('?')[0].split('#')[0]
  try {
    path = decodeURI(path)
  } catch {
    /* garde tel quel */
  }
  if (!path.startsWith('/')) path = '/' + path
  if (!path.endsWith('/')) path += '/'
  return path
}

export function getService(path: string): Service | undefined {
  return serviceByPath.get(normalizePath(path))
}
export function getArticle(path: string): Article | undefined {
  return articleByPath.get(normalizePath(path))
}
export function getCategory(path: string): Category | undefined {
  return categoryByPath.get(normalizePath(path))
}
export function getCategoryById(id: number): Category | undefined {
  return categoryById.get(id)
}
export function getAuthor(id?: number | null): Author | undefined {
  return id ? authorById.get(id) : undefined
}

/** Resout un chemin vers un type de page + son entite. */
export function resolvePath(rawPath: string): Resolved {
  const path = normalizePath(rawPath)

  if (path === PATHS.home) return { kind: 'home', service: serviceByPath.get('/') }
  if (path === PATHS.contact) return { kind: 'contact', service: serviceByPath.get(path) }
  if (path === PATHS.merci) return { kind: 'merci', service: serviceByPath.get(path) }
  if (path === PATHS.mentions) return { kind: 'mentions', service: serviceByPath.get(path) }
  if (path === PATHS.blog) return { kind: 'blog', service: serviceByPath.get(path) }
  if (path === PATHS.quiSommesNous)
    return { kind: 'qui-sommes-nous', service: serviceByPath.get(path) }

  const service = serviceByPath.get(path)
  if (service) return { kind: 'service', service }

  // Articles et categories : servis EN DIRECT (lib/blog.ts), plus resolus depuis le JSON
  // du build, qui serait fige a la date du dernier build. Le resolveur de page consulte
  // d'abord les donnees live ; un chemin absent ici et du blog live est une vraie 404.
  return { kind: 'not-found' }
}

/** Articles d'une categorie, tries du plus recent au plus ancien. */
export function articlesInCategory(categoryId: number): Article[] {
  return articles
    .filter((a) => a.category_ids?.includes(categoryId))
    .sort((a, b) => (b.date || '').localeCompare(a.date || ''))
}

/** Tous les articles tries par date desc (liste blog). */
export function articlesSorted(): Article[] {
  return [...articles].sort((a, b) => (b.date || '').localeCompare(a.date || ''))
}

/** Articles lies : meme categorie, hors article courant. */
export function relatedArticles(article: Article, limit = 3): Article[] {
  const cats = new Set(article.category_ids || [])
  const sorted = articlesSorted()
  const sameCat = sorted.filter(
    (a) => a.path !== article.path && a.category_ids?.some((c) => cats.has(c)),
  )
  if (sameCat.length >= limit) return sameCat.slice(0, limit)
  // Repli : complete avec les articles recents pour toujours proposer `limit` lectures.
  const chosen = new Set(sameCat.map((a) => a.path))
  chosen.add(article.path)
  const fill = sorted.filter((a) => !chosen.has(a.path))
  return [...sameCat, ...fill].slice(0, limit)
}

export function primaryCategory(article: Article): Category | undefined {
  if (article.primary_category_id != null && categoryById.has(article.primary_category_id)) {
    return categoryById.get(article.primary_category_id)
  }
  const id = (article.category_ids || []).find((c) => categoryById.has(c))
  return id ? categoryById.get(id) : undefined
}

/** Categories non vides, triees par nom (pour les filtres : jamais de choix vide). */
export function nonEmptyCategories(): Category[] {
  return categories
    .filter((c) => c.count > 0 && c.slug !== 'a-classer')
    .sort((a, b) => a.name.localeCompare(b.name, 'fr'))
}

// --- Zones d'intervention (section 'zone', chemins /interventions/{slug}/) ---

export interface ZoneGroup {
  sector: string
  zones: Service[]
}

/** Toutes les pages d'interventions locales, triees par titre. */
export function zoneServices(): Service[] {
  return services
    .filter((s) => s.section === 'zone')
    .sort((a, b) => (a.title || '').localeCompare(b.title || '', 'fr'))
}

/**
 * Departement d'une zone, deduit du code postal present dans le seo_title
 * (ex. "... a La Baule (44500) ..."). Repli sur null si introuvable.
 */
function zoneDepartment(s: Service): string | null {
  const hay = `${s.seo?.seo_title || ''} ${s.seo?.seo_description || ''} ${s.title || ''}`
  const m = hay.match(/(\d{2})\d{3}/)
  return m ? m[1] : null
}

// Ordre et libelles des secteurs. On garde le berceau historique (53) en tete.
const SECTOR_ORDER: { dept: string; label: string }[] = [
  { dept: '53', label: 'Mayenne et Laval' },
  { dept: '44', label: 'La Baule et Loire-Atlantique' },
]

/**
 * Zones regroupees par secteur pour le maillage interne (footer). Jamais de
 * groupe vide : un secteur sans zone n'apparait pas. Les zones sans departement
 * identifiable sont rassemblees dans un groupe "Autres secteurs".
 */
export function zonesBySector(): ZoneGroup[] {
  const all = zoneServices()
  const byDept = new Map<string, Service[]>()
  const orphans: Service[] = []
  for (const z of all) {
    const dept = zoneDepartment(z)
    if (dept) {
      if (!byDept.has(dept)) byDept.set(dept, [])
      byDept.get(dept)!.push(z)
    } else {
      orphans.push(z)
    }
  }
  const groups: ZoneGroup[] = []
  for (const { dept, label } of SECTOR_ORDER) {
    const zones = byDept.get(dept)
    if (zones?.length) groups.push({ sector: label, zones })
  }
  // Departements non prevus dans l'ordre (robustesse si le perimetre s'etend).
  for (const [dept, zones] of byDept) {
    if (!SECTOR_ORDER.some((s) => s.dept === dept) && zones.length) {
      groups.push({ sector: `Departement ${dept}`, zones })
    }
  }
  if (orphans.length) groups.push({ sector: 'Autres secteurs', zones: orphans })
  return groups
}

// --- Navigation : recherche d'un noeud + freres/enfants (maillage interne) ---

interface NavHit {
  node: NavItem
  parent: NavItem | null
  ancestors: NavItem[]
}

function findNav(
  path: string,
  items: NavItem[] = navFor(path),
  parent: NavItem | null = null,
  ancestors: NavItem[] = [],
): NavHit | null {
  for (const node of items) {
    if (node.path === path) return { node, parent, ancestors }
    if (node.children) {
      const hit = findNav(path, node.children, node, [...ancestors, node])
      if (hit) return hit
    }
  }
  return null
}

/** Liens de maillage : enfants du service, sinon freres dans la navigation. */
export function relatedServiceLinks(path: string): NavItem[] {
  const menu = navFor(path)
  const hit = findNav(normalizePath(path), menu)
  if (!hit) return []
  if (hit.node.children?.length) return hit.node.children
  if (hit.parent?.children?.length) return hit.parent.children.filter((c) => c.path !== hit.node.path)
  return menu.filter((n) => n.path !== hit.node.path && n.children)
}

/** Fil d'ariane pour un service, base sur ses chemins ancetres reels. */
export function serviceBreadcrumb(service: Service): { label: string; path: string }[] {
  const crumbs: { label: string; path: string }[] = [{ label: 'Accueil', path: '/' }]
  const segments = service.path.replace(/^\/|\/$/g, '').split('/')
  let acc = ''
  for (let i = 0; i < segments.length; i++) {
    acc += '/' + segments[i]
    const p = acc + '/'
    const s = serviceByPath.get(p)
    if (p === service.path) {
      crumbs.push({ label: service.h1 || service.title, path: p })
    } else if (s) {
      crumbs.push({ label: s.title, path: p })
    }
  }
  return crumbs
}
